import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { FacilitatorGuideContent } from '@/components/FacilitatorGuideContent';
import {
  ShieldCheck, Key, Users, Plus, Copy, Check, RefreshCw,
  AlertCircle, Loader2, LogOut, KeyRound, Download, GraduationCap, BookOpen
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const ADMIN_KEY_STORAGE = 'admin_key';

function AdminDashboard() {
  const [adminKey, setAdminKey] = useState('');
  const [authed, setAuthed] = useState(false);
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  const [tab, setTab] = useState('codes');
  const [codes, setCodes] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generateCount, setGenerateCount] = useState(5);
  const [genType, setGenType] = useState('student'); // 'student' | 'facilitator'
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState('');
  const [error, setError] = useState('');
  const [labelDraft, setLabelDraft] = useState({});
  const [resetResult, setResetResult] = useState(null); // { email, password }
  const [resettingEmail, setResettingEmail] = useState('');
  const [togglingEmail, setTogglingEmail] = useState('');
  const [resetRequests, setResetRequests] = useState([]);

  const fetchData = useCallback(async function (key) {
    setLoading(true);
    setError('');
    try {
      const headers = { 'x-admin-key': key };
      const [codesRes, usersRes, reqRes] = await Promise.all([
        fetch(API_URL + '/api/admin/codes', { headers: headers }),
        fetch(API_URL + '/api/admin/users', { headers: headers }),
        fetch(API_URL + '/api/admin/reset-requests', { headers: headers })
      ]);
      if (!codesRes.ok || !usersRes.ok) {
        throw new Error('Failed to load data');
      }
      const codesData = await codesRes.json();
      const usersData = await usersRes.json();
      const reqData = reqRes.ok ? await reqRes.json() : [];
      // newest first
      setCodes(codesData.slice().reverse());
      setUsers(usersData.slice().reverse());
      setResetRequests(reqData);
    } catch (err) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(function () {
    const saved = sessionStorage.getItem(ADMIN_KEY_STORAGE);
    if (saved) {
      setAdminKey(saved);
      setAuthed(true);
      fetchData(saved);
    }
  }, [fetchData]);

  async function handleAdminLogin(e) {
    e.preventDefault();
    setAuthError('');
    setAuthLoading(true);
    try {
      const res = await fetch(API_URL + '/api/admin/codes', {
        headers: { 'x-admin-key': adminKey }
      });
      if (res.status === 403) {
        throw new Error('Incorrect admin password');
      }
      if (!res.ok) {
        throw new Error('Unable to reach the server');
      }
      sessionStorage.setItem(ADMIN_KEY_STORAGE, adminKey);
      setAuthed(true);
      await fetchData(adminKey);
    } catch (err) {
      setAuthError(err.message);
    } finally {
      setAuthLoading(false);
    }
  }

  function handleAdminLogout() {
    sessionStorage.removeItem(ADMIN_KEY_STORAGE);
    setAuthed(false);
    setAdminKey('');
    setCodes([]);
    setUsers([]);
  }

  async function handleGenerate() {
    const count = parseInt(generateCount, 10);
    if (!count || count < 1 || count > 100) {
      setError('Please enter a number between 1 and 100');
      return;
    }
    setGenerating(true);
    setError('');
    try {
      const res = await fetch(API_URL + '/api/admin/generate-codes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-key': adminKey
        },
        body: JSON.stringify({ count: count, code_type: genType })
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.detail || 'Failed to generate codes');
      }
      await fetchData(adminKey);
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  }

  async function handleToggleFacilitator(userEmail, currentAccess) {
    setTogglingEmail(userEmail);
    setError('');
    try {
      const res = await fetch(API_URL + '/api/admin/users/' + encodeURIComponent(userEmail) + '/facilitator', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'x-admin-key': adminKey },
        body: JSON.stringify({ access: !currentAccess })
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.detail || 'Failed to update license');
      }
      await fetchData(adminKey);
    } catch (err) {
      setError(err.message);
    } finally {
      setTogglingEmail('');
    }
  }

  function handleCopy(text) {
    navigator.clipboard.writeText(text);
    setCopied(text);
    setTimeout(function () { setCopied(''); }, 1500);
  }

  async function handleResetPassword(userEmail) {
    const custom = window.prompt(
      'Set a new password for ' + userEmail + ' (at least 6 characters).\n\n' +
      'Leave blank and click OK to auto-generate a random temporary password instead.'
    );
    if (custom === null) return; // cancelled
    const trimmed = custom.trim();
    if (trimmed && trimmed.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setResettingEmail(userEmail);
    setResetResult(null);
    setError('');
    try {
      const res = await fetch(API_URL + '/api/admin/users/' + encodeURIComponent(userEmail) + '/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-admin-key': adminKey },
        body: JSON.stringify(trimmed ? { new_password: trimmed } : {})
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Reset failed');
      }
      setResetResult({ email: data.email, password: data.temporary_password });
      fetchData(adminKey);
    } catch (err) {
      setError(err.message);
    } finally {
      setResettingEmail('');
    }
  }

  async function handleDismissRequest(reqEmail) {
    try {
      await fetch(API_URL + '/api/admin/reset-requests/' + encodeURIComponent(reqEmail), {
        method: 'DELETE',
        headers: { 'x-admin-key': adminKey }
      });
      setResetRequests(function (prev) { return prev.filter(function (r) { return r.email !== reqEmail; }); });
    } catch (err) {
      setError('Failed to dismiss request');
    }
  }

  function getDraft(c) {
    if (labelDraft[c.code]) return labelDraft[c.code];
    return { name: c.assigned_to_name || '', email: c.assigned_to_email || '' };
  }

  function setDraft(code, field, value) {
    setLabelDraft(function (prev) {
      const cur = prev[code] || {};
      const next = {};
      next[code] = Object.assign({}, cur, {});
      // ensure both fields exist
      const existing = prev[code] || {};
      next[code].name = field === 'name' ? value : (existing.name !== undefined ? existing.name : '');
      next[code].email = field === 'email' ? value : (existing.email !== undefined ? existing.email : '');
      return Object.assign({}, prev, next);
    });
  }

  async function saveLabel(c) {
    const draft = getDraft(c);
    if ((draft.name || '') === (c.assigned_to_name || '') && (draft.email || '') === (c.assigned_to_email || '')) {
      return; // no change
    }
    try {
      const res = await fetch(API_URL + '/api/admin/codes/' + c.code + '/label', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'x-admin-key': adminKey },
        body: JSON.stringify({ assigned_to_name: draft.name || null, assigned_to_email: draft.email || null })
      });
      if (res.ok) {
        setCodes(function (prev) {
          return prev.map(function (item) {
            if (item.code === c.code) {
              return Object.assign({}, item, { assigned_to_name: draft.name || null, assigned_to_email: draft.email || null });
            }
            return item;
          });
        });
      }
    } catch (err) {
      setError('Failed to save label');
    }
  }

  function handleCopyAllUnused() {
    const unused = codes.filter(function (c) { return !c.used; }).map(function (c) { return c.code; });
    if (unused.length === 0) return;
    handleCopy(unused.join('\n'));
  }

  function handleExportCsv() {
    const rows = [['Code', 'Type', 'Assigned To', 'Assigned Email', 'Status', 'Used By', 'Created At', 'Used At']];
    codes.forEach(function (c) {
      rows.push([
        c.code,
        c.code_type || 'student',
        c.assigned_to_name || '',
        c.assigned_to_email || '',
        c.used ? 'Used' : 'Available',
        c.used_by || '',
        c.created_at || '',
        c.used_at || ''
      ]);
    });
    const csv = rows.map(function (r) {
      return r.map(function (v) { return '"' + String(v).replace(/"/g, '""') + '"'; }).join(',');
    }).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'access-codes.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  function formatDate(iso) {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (e) {
      return iso;
    }
  }

  // ============ LOGIN SCREEN ============
  if (!authed) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="admin-login-screen">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <ShieldCheck className="h-6 w-6 text-primary" />
              </div>
            </div>
            <h1 className="text-2xl font-serif font-bold text-foreground">Admin Console</h1>
            <p className="text-sm text-muted-foreground mt-1">The Rules of Money Workbook</p>
          </div>

          <div className="bg-card border border-border rounded-xl p-6 shadow-card">
            <h2 className="text-xl font-semibold mb-6 text-center">Enter Admin Password</h2>

            {authError && (
              <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-2 text-sm text-destructive" data-testid="admin-login-error">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{authError}</span>
              </div>
            )}

            <form onSubmit={handleAdminLogin} className="space-y-4">
              <div>
                <Label htmlFor="adminKey" className="flex items-center gap-2 mb-2">
                  <KeyRound className="h-4 w-4" />
                  Admin Password
                </Label>
                <Input
                  id="adminKey"
                  type="password"
                  placeholder="••••••••"
                  value={adminKey}
                  onChange={function (e) { setAdminKey(e.target.value); }}
                  required
                  data-testid="admin-key-input"
                />
              </div>
              <Button type="submit" className="w-full" disabled={authLoading} data-testid="admin-login-button">
                {authLoading ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Verifying...</>
                ) : 'Access Dashboard'}
              </Button>
            </form>
          </div>

          <p className="text-center text-xs text-muted-foreground mt-6">
            For workbook administrators only.
          </p>
        </div>
      </div>
    );
  }

  // ============ DASHBOARD ============
  const usedCount = codes.filter(function (c) { return c.used; }).length;
  const availableCount = codes.length - usedCount;

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
              <ShieldCheck className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="font-serif font-bold text-foreground leading-tight">Admin Console</h1>
              <p className="text-xs text-muted-foreground">The Rules of Money Workbook</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={handleAdminLogout} data-testid="admin-logout-button">
            <LogOut className="h-4 w-4 mr-2" /> Sign Out
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <StatCard icon={<Key className="h-5 w-5 text-primary" />} label="Total Codes" value={codes.length} testId="stat-total-codes" />
          <StatCard icon={<Plus className="h-5 w-5 text-emerald-600" />} label="Available" value={availableCount} testId="stat-available-codes" />
          <StatCard icon={<Users className="h-5 w-5 text-amber-600" />} label="Registered Students" value={users.length} testId="stat-registered-users" />
        </div>

        {error && (
          <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-2 text-sm text-destructive" data-testid="admin-error">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Generate Section */}
        <div className="bg-card border border-border rounded-xl p-5 mb-8 shadow-card">
          <h2 className="font-semibold mb-3 flex items-center gap-2"><Plus className="h-4 w-4" /> Generate Access Codes</h2>
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-32">
              <Label htmlFor="genCount" className="mb-2 block text-sm">How many?</Label>
              <Input
                id="genCount"
                type="number"
                min={1}
                max={100}
                value={generateCount}
                onChange={function (e) { setGenerateCount(e.target.value); }}
                data-testid="generate-count-input"
              />
            </div>
            <div>
              <Label className="mb-2 block text-sm">Type</Label>
              <div className="flex rounded-lg border border-border overflow-hidden">
                <button
                  type="button"
                  onClick={function () { setGenType('student'); }}
                  className={'px-3 py-2 text-sm flex items-center gap-1.5 ' + (genType === 'student' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground')}
                  data-testid="gentype-student"
                >
                  <GraduationCap className="h-4 w-4" /> Student
                </button>
                <button
                  type="button"
                  onClick={function () { setGenType('facilitator'); }}
                  className={'px-3 py-2 text-sm flex items-center gap-1.5 ' + (genType === 'facilitator' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground')}
                  data-testid="gentype-facilitator"
                >
                  <BookOpen className="h-4 w-4" /> Facilitator
                </button>
              </div>
            </div>
            <Button onClick={handleGenerate} disabled={generating} data-testid="generate-codes-button">
              {generating ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating...</>
              ) : (
                <><Plus className="h-4 w-4 mr-2" /> Generate</>
              )}
            </Button>
            <p className="text-xs text-muted-foreground max-w-[200px]">
              {genType === 'facilitator'
                ? 'Facilitator codes unlock the Facilitator Guide for the buyer.'
                : 'Student codes give standard workbook access.'}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-4 border-b border-border">
          <TabButton active={tab === 'codes'} onClick={function () { setTab('codes'); }} testId="tab-codes">
            Access Codes ({codes.length})
          </TabButton>
          <TabButton active={tab === 'users'} onClick={function () { setTab('users'); }} testId="tab-users">
            <span className="flex items-center gap-1.5">
              Students ({users.length})
              {resetRequests.length > 0 && (
                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-white text-[10px] font-bold" data-testid="reset-request-badge">
                  {resetRequests.length}
                </span>
              )}
            </span>
          </TabButton>
          <TabButton active={tab === 'guide'} onClick={function () { setTab('guide'); }} testId="tab-guide">
            Facilitator Guide
          </TabButton>
          <div className="ml-auto flex items-center gap-2 pb-2">
            <Button variant="ghost" size="sm" onClick={function () { fetchData(adminKey); }} disabled={loading} data-testid="refresh-button">
              <RefreshCw className={'h-4 w-4 ' + (loading ? 'animate-spin' : '')} />
            </Button>
          </div>
        </div>

        {/* Codes Tab */}
        {tab === 'codes' && (
          <div data-testid="codes-panel">
            <div className="flex flex-wrap gap-2 mb-4">
              <Button variant="outline" size="sm" onClick={handleCopyAllUnused} disabled={availableCount === 0} data-testid="copy-all-unused-button">
                <Copy className="h-4 w-4 mr-2" /> Copy all available ({availableCount})
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportCsv} disabled={codes.length === 0} data-testid="export-csv-button">
                <Download className="h-4 w-4 mr-2" /> Export CSV
              </Button>
            </div>

            {loading && codes.length === 0 ? (
              <LoadingState />
            ) : codes.length === 0 ? (
              <EmptyState text="No codes yet. Generate some above to get started." />
            ) : (
              <div className="bg-card border border-border rounded-xl overflow-hidden shadow-card">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-muted/40 text-left text-muted-foreground">
                        <th className="px-4 py-3 font-medium">Code</th>
                        <th className="px-4 py-3 font-medium">Assigned To</th>
                        <th className="px-4 py-3 font-medium">Status</th>
                        <th className="px-4 py-3 font-medium">Used By</th>
                        <th className="px-4 py-3 font-medium">Created</th>
                        <th className="px-4 py-3 font-medium text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {codes.map(function (c) {
                        return (
                          <tr key={c.code} className="border-b border-border last:border-0 hover:bg-muted/30" data-testid={'code-row-' + c.code}>
                            <td className="px-4 py-3 font-mono tracking-wider font-medium">
                              <div className="flex items-center gap-2">
                                <span>{c.code}</span>
                                {c.code_type === 'facilitator' && (
                                  <Badge className="bg-violet-100 text-violet-700 hover:bg-violet-100 border-transparent text-[10px] font-sans" data-testid={'code-type-' + c.code}>
                                    Facilitator
                                  </Badge>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex flex-col gap-1 min-w-[160px]">
                                <input
                                  type="text"
                                  placeholder="Name / label"
                                  value={getDraft(c).name}
                                  onChange={function (e) { setDraft(c.code, 'name', e.target.value); }}
                                  onBlur={function () { saveLabel(c); }}
                                  className="w-full px-2 py-1 text-xs rounded border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                                  data-testid={'label-name-' + c.code}
                                />
                                <input
                                  type="email"
                                  placeholder="Email (optional)"
                                  value={getDraft(c).email}
                                  onChange={function (e) { setDraft(c.code, 'email', e.target.value); }}
                                  onBlur={function () { saveLabel(c); }}
                                  className="w-full px-2 py-1 text-xs rounded border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                                  data-testid={'label-email-' + c.code}
                                />
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              {c.used ? (
                                <Badge variant="secondary">Used</Badge>
                              ) : (
                                <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-transparent">Available</Badge>
                              )}
                            </td>
                            <td className="px-4 py-3 text-muted-foreground">{c.used_by || '—'}</td>
                            <td className="px-4 py-3 text-muted-foreground">{formatDate(c.created_at)}</td>
                            <td className="px-4 py-3 text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={function () { handleCopy(c.code); }}
                                data-testid={'copy-code-' + c.code}
                              >
                                {copied === c.code ? (
                                  <><Check className="h-4 w-4 mr-1 text-emerald-600" /> Copied</>
                                ) : (
                                  <><Copy className="h-4 w-4 mr-1" /> Copy</>
                                )}
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Users Tab */}
        {tab === 'users' && (
          <div data-testid="users-panel">
            {resetRequests.length > 0 && (
              <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="reset-requests-banner">
                <p className="font-medium text-amber-800 text-sm mb-2 flex items-center gap-2">
                  <KeyRound className="h-4 w-4" />
                  {resetRequests.length} password reset {resetRequests.length === 1 ? 'request' : 'requests'}
                </p>
                <div className="space-y-2">
                  {resetRequests.map(function (r) {
                    return (
                      <div key={r.email} className="flex items-center justify-between gap-3 bg-white rounded-md border border-amber-200 px-3 py-2" data-testid={'reset-request-' + r.email}>
                        <span className="text-sm font-medium truncate">{r.email}</span>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button variant="outline" size="sm" onClick={function () { handleResetPassword(r.email); }} disabled={resettingEmail === r.email} data-testid={'request-reset-' + r.email}>
                            {resettingEmail === r.email ? <Loader2 className="h-4 w-4 animate-spin" /> : <><KeyRound className="h-4 w-4 mr-1" /> Reset</>}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={function () { handleDismissRequest(r.email); }} data-testid={'request-dismiss-' + r.email}>
                            Dismiss
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {resetResult && (
              <div className="mb-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg flex items-start justify-between gap-3" data-testid="reset-result">
                <div className="text-sm">
                  <p className="font-medium text-emerald-800">Temporary password for {resetResult.email}</p>
                  <p className="text-emerald-700 mt-1">
                    Share this with the student. They'll use it to log in:
                    <span className="font-mono font-bold ml-2 bg-white px-2 py-0.5 rounded border border-emerald-200">{resetResult.password}</span>
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button variant="outline" size="sm" onClick={function () { handleCopy(resetResult.password); }} data-testid="copy-temp-password">
                    {copied === resetResult.password ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={function () { setResetResult(null); }} data-testid="dismiss-reset-result">Dismiss</Button>
                </div>
              </div>
            )}
            {loading && users.length === 0 ? (
              <LoadingState />
            ) : users.length === 0 ? (
              <EmptyState text="No students have registered yet." />
            ) : (
              <div className="bg-card border border-border rounded-xl overflow-hidden shadow-card">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-muted/40 text-left text-muted-foreground">
                        <th className="px-4 py-3 font-medium">Email</th>
                        <th className="px-4 py-3 font-medium">License</th>
                        <th className="px-4 py-3 font-medium">Registered</th>
                        <th className="px-4 py-3 font-medium text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map(function (u) {
                        return (
                          <tr key={u.id} className="border-b border-border last:border-0 hover:bg-muted/30" data-testid={'user-row-' + u.email}>
                            <td className="px-4 py-3 font-medium">{u.email}</td>
                            <td className="px-4 py-3">
                              {u.facilitator_access ? (
                                <Badge className="bg-violet-100 text-violet-700 hover:bg-violet-100 border-transparent" data-testid={'license-' + u.email}>Facilitator</Badge>
                              ) : (
                                <Badge variant="secondary" data-testid={'license-' + u.email}>Student</Badge>
                              )}
                            </td>
                            <td className="px-4 py-3 text-muted-foreground">{formatDate(u.created_at)}</td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={function () { handleToggleFacilitator(u.email, u.facilitator_access); }}
                                  disabled={togglingEmail === u.email}
                                  data-testid={'toggle-facilitator-' + u.email}
                                >
                                  {togglingEmail === u.email ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <><BookOpen className="h-4 w-4 mr-1" /> {u.facilitator_access ? 'Revoke' : 'Make Facilitator'}</>
                                  )}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={function () { handleResetPassword(u.email); }}
                                  disabled={resettingEmail === u.email}
                                  data-testid={'reset-password-' + u.email}
                                >
                                  {resettingEmail === u.email ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <><KeyRound className="h-4 w-4 mr-1" /> Reset</>
                                  )}
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Facilitator Guide Tab */}
        {tab === 'guide' && (
          <div data-testid="guide-panel">
            <div className="bg-card border border-border rounded-xl p-6 shadow-card">
              <div className="mb-6 p-3 bg-violet-50 border border-violet-200 rounded-lg text-sm text-violet-800">
                This guide is visible to you and to anyone with a <strong>Facilitator license</strong>.
                Regular students don't see it.
              </div>
              <FacilitatorGuideContent />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function StatCard(props) {
  return (
    <div className="bg-card border border-border rounded-xl p-5 shadow-card flex items-center gap-4" data-testid={props.testId}>
      <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
        {props.icon}
      </div>
      <div>
        <p className="text-2xl font-bold text-foreground leading-none">{props.value}</p>
        <p className="text-sm text-muted-foreground mt-1">{props.label}</p>
      </div>
    </div>
  );
}

function TabButton(props) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      data-testid={props.testId}
      className={
        'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ' +
        (props.active
          ? 'border-primary text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground')
      }
    >
      {props.children}
    </button>
  );
}

function LoadingState() {
  return (
    <div className="bg-card border border-border rounded-xl p-12 text-center shadow-card">
      <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
    </div>
  );
}

function EmptyState(props) {
  return (
    <div className="bg-card border border-border rounded-xl p-12 text-center text-muted-foreground shadow-card" data-testid="empty-state">
      {props.text}
    </div>
  );
}

export { AdminDashboard };
